// src/analytics/track.js

const CURRENCY = 'EUR';

/**
 * Round a number to 2 decimal places.
 */
const round = (n) => Math.round(Number(n) * 100) / 100;

/**
 * Push an ecommerce event to the dataLayer.
 *
 * Always clears the previous ecommerce object first so
 * previous event data cannot leak into the next event.
 */
function push(event, ecommerce, extra = {}) {
  window.dataLayer = window.dataLayer || [];

  // Clear previous ecommerce data
  window.dataLayer.push({ ecommerce: null });

  // Push current event
  window.dataLayer.push({
    event,
    ecommerce,
    ...extra,
  });
}

/**
 * Convert a SuperGallery product/configuration into
 * the standard GA4 ecommerce item structure.
 *
 * config can contain:
 * - sizeName
 * - finishingName
 * - frameName
 * - unitPrice
 */
export function toItem(product, config = {}, quantity = 1, extra = {}) {
  if (!product) {
    return null;
  }

  const variant = [
    config.finishingName,
    config.sizeName,
    config.frameName,
  ]
    .filter(Boolean)
    .join(' / ');

  return {
    item_id: String(product.reference || product.id),
    item_name: product.name,

    ...(product.photographer
      ? { item_brand: product.photographer }
      : {}),

    ...(product.collection || product.category
      ? {
          item_category:
            product.collection || product.category,
        }
      : {}),

    ...(config.finishingName
      ? {
          item_category2: config.finishingName,
        }
      : {}),

    ...(config.frameName
      ? {
          item_category3: config.frameName,
        }
      : {}),

    ...(variant
      ? {
          item_variant: variant,
        }
      : {}),

    price: round(
      config.unitPrice ?? product.price
    ),

    quantity: Number(quantity),

    ...extra,
  };
}

/**
 * Calculate the total value of ecommerce items.
 */
const sum = (items = []) =>
  round(
    items.reduce(
      (total, item) =>
        total +
        Number(item.price || 0) *
          Number(item.quantity || 0),
      0
    )
  );

/**
 * Convert cart lines into GA4 items.
 *
 * Expected:
 * cart.lines = [
 *   {
 *     product,
 *     config,
 *     quantity
 *   }
 * ]
 */
const cartItems = (cart) => {
  const products = Array.isArray(cart?.products)
    ? cart.products
    : [];

  return products
    .map((product) => {
      if (!product) {
        return null;
      }

      const attributes = product.attributes || {};

      const variant = [
        attributes.Finishing,
        attributes.Sizing,
        attributes.Frames,
      ]
        .filter(Boolean)
        .map((value) => String(value).trim())
        .filter(Boolean)
        .join(" / ");

      return {
        item_id: String(
          product.reference || product.id_product 
        ),

        item_name: product.name || "",
         ...(product.photographer
      ? { item_brand: product.photographer }
      : {}),

    
        ...(product.reference
          ? {
              item_variant: variant || undefined,
            }
          : {
              item_variant: variant || undefined,
            }),

        ...(product.category_name || product.category
          ? {
              item_category:
                product.category_name ||
                product.category,
            }
          : {}),

        price: round(
          Number(
            product.price_amount ??
            product.price_wt ??
            product.price ??
            0
          )
        ),

        quantity: Number(
          product.cart_quantity ??
          product.quantity_wanted ??
          product.quantity ??
          1
        ),
      };
    })
    .filter(Boolean);
};

/**
 * Product detail page.
 */
export function trackViewItem(product, config = {}) {
  const item = toItem(product, config, 1);

  if (!item) {
    return;
  }

  const items = [item];

  push('view_item', {
    currency: CURRENCY,
    value: sum(items),
    items,
  });
}

/**
 * Add product to cart.
 *
 * Should only be called AFTER the add-to-cart API succeeds.
 */

export function trackAddToCart(product, config = {}) {
  if (!product) return;

  const quantity = Number(config.quantity) || 1;

  const price = round(
    config.unitPrice ??
    product.price ??
    0
  );

  // Get Photographer from features
  const photographer =
    product.features?.find(
      (feature) => feature.name === "Photographer"
    )?.value || "";

  // Get Collection from features
  const collection =
    product.features?.find(
      (feature) => feature.name === "Collection"
    )?.value || product.category_name || "";

  // Build selected configuration
  const variant = [
    // config.typeName,
    config.finishingName,
    config.sizeName,
    config.frameName,
  ]
    .filter(Boolean)
    .join(" / ");

  const item = {
    item_id: String(
      product.reference ||
      product.product_reference ||
      ""
    ),

    item_name: product.name || "",

    ...(photographer
      ? {
          item_brand: photographer,
        }
      : {}),

    ...(collection
      ? {
          item_category: collection,
        }
      : {}),

    ...(variant
      ? {
          item_variant: variant,
        }
      : {}),

    price,
    quantity,
  };

  // Don't send event if product reference is missing
  if (!item.item_id) {
    console.warn(
      "GA4 add_to_cart: product reference missing",
      product
    );
    return;
  }

  push("add_to_cart", {
    currency: CURRENCY,
    value: round(price * quantity),
    items: [item],
  });
}



/**
 * Remove product from cart.
 *
 * Should only be called AFTER the remove/update API succeeds.
 */
export function trackRemoveFromCart(cartLine, quantityRemoved = 1) {
  if (!cartLine) {
    return;
  }

  const quantity = Number(quantityRemoved) || 1;

  const price = round(
    cartLine.price_amount ??
    cartLine.price ??
    cartLine.unit_price ??
    0
  );
    const attributes = cartLine.attributes || {};

      const variant = [
        attributes.Finishing,
        attributes.Sizing,
        attributes.Frames,
      ]
        .filter(Boolean)
        .map((value) => String(value).trim())
        .filter(Boolean)
        .join(" / ");
  const item = {
    item_id: String(
      cartLine.reference ||
      cartLine.product_reference ||
      cartLine.id_product ||
      cartLine.id ||
      ""
    ),

    item_name:
      cartLine.name ||
      cartLine.product_name ||
      "",
    ...(cartLine.reference
          ? {
              item_variant: variant || undefined,
            }
          : {
              item_variant: variant || undefined,
            }),
    ...(cartLine.category_name
      ? {
          item_category: cartLine.category_name,
        }
      : {}),

    ...(cartLine.photographer
      ? {
          item_brand: cartLine.photographer,
        }
      : {}),

    price,
    quantity,
  };

  if (!item.item_id) {
    console.warn(
      "GA4 remove_from_cart: product reference missing",
      cartLine
    );
    return;
  }

  push("remove_from_cart", {
    currency: CURRENCY,
    value: round(price * quantity),
    items: [item],
  });
}

/**
 * Add product to wishlist.
 *
 * Should only be called AFTER the wishlist API succeeds.
 */
export function trackAddToWishlist(
  product,
  config = {}
) {
  const item = toItem(product, config, 1);

  if (!item) {
    return;
  }

  const items = [item];

  push('add_to_wishlist', {
    currency: CURRENCY,
    value: sum(items),
    items,
  });
}

/**
 * View cart / mini-cart.
 */
export function trackViewCart(cart) {
  const items = cartItems(cart);

  push('view_cart', {
    currency: CURRENCY,
    value: sum(items),
    items,
  });
}

/**
 * Begin checkout.
 */
export function trackBeginCheckout(cart) {
  const items = cartItems(cart);

  push("begin_checkout", {
    currency: CURRENCY,
    value: sum(items),
    coupon: cart?.voucherCode || undefined,
    items,
  });
}

/**
 * Shipping method confirmed.
 */
export function trackAddShippingInfo(
  cart,
  carrierName
) {
  const items = cartItems(cart);

  push('add_shipping_info', {
    currency: CURRENCY,
    value: sum(items),
    shipping_tier: carrierName,
    items,
  });
}

/**
 * Payment method selected.
 */
export function trackAddPaymentInfo(cart, paymentType) {
  if (!cart || !paymentType) {
    return;
  }

  const products = Array.isArray(cart.products)
    ? cart.products
    : [];

  const items = products
    .map((product) => {
      const quantity =
        Number(product.quantity || product.cart_quantity) || 1;

      const price = round(
        Number(
          product.price_amount ??
          product.price_wt ??
          product.price_with_reduction ??
          0
        )
      );

      const itemId =
        product.reference ||
        product.reference_to_display ||
        product.embedded_attributes?.reference ||
        "";

      if (!itemId) {
        
        return null;
      }

      const attributes = product.attributes || {};

      const variant = [
        attributes.Finishing,
        attributes.Sizing,
        attributes.Frames,
      ]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
        .join(" / ");

      return {
        item_id: String(itemId),
        item_name: product.name || "",
        ...(variant ? { item_variant: variant } : {}),
        ...(product.category_name
          ? { item_category: product.category_name }
          : {}),
        price,
        quantity,
      };
    })
    .filter(Boolean);

  if (!items.length) {
    
    return;
  }

  const value = items.reduce(
    (total, item) =>
      total +
      Number(item.price || 0) *
        Number(item.quantity || 1),
    0
  );

  push("add_payment_info", {
    currency: cart.currency?.iso_code || CURRENCY,
    value: round(value),
    payment_type: paymentType,
    items: cartItems(cart),
  });
}

/**
 * Product/category/search/home product list.
 *
 * Example list IDs:
 * - collection_slim-aarons
 * - photographer_slim-aarons
 * - search_results
 * - home_bestsellers
 */
export function trackViewItemList(
  listId,
  listName,
  products = []
) {
  if (!Array.isArray(products)) {
    return;
  }

  const items = products
    .map((product, index) =>
      toItem(
        product,
        {},
        1,
        {
          index,
          item_list_id: listId,
          item_list_name: listName,
        }
      )
    )
    .filter(Boolean);

  push('view_item_list', {
    item_list_id: listId,
    item_list_name: listName,
    items,
  });
}

/**
 * Product tile selection.
 *
 * Should be called before navigating to the product page.
 */
export function trackSelectItem(
  listId,
  listName,
  product,
  index
) {
  const item = toItem(
    product,
    {},
    1,
    {
      index,
      item_list_id: listId,
      item_list_name: listName,
    }
  );

  if (!item) {
    return;
  }

  const items = [item];

  push('select_item', {
    item_list_id: listId,
    item_list_name: listName,
    items,
  });
}

/**
 * Purchase event.
 *
 * Should only be called when the order is confirmed/paid/accepted.
 *
 * Purchase is protected by localStorage so refreshing the
 * confirmation page does not send the same purchase again.
 */

function parsePrice(value) {
  if (value === null || value === undefined) {
    return 0;
  }

  let str = String(value)
    .replace(/[€$£\s]/g, "")
    .trim();

  // Handles:
  // 2585.74
  // 2,585.74
  // 2.585,74
  if (str.includes(",") && str.includes(".")) {
    const lastComma = str.lastIndexOf(",");
    const lastDot = str.lastIndexOf(".");

    if (lastComma > lastDot) {
      // European format: 2.585,74
      str = str.replace(/\./g, "").replace(",", ".");
    } else {
      // US format: 2,585.74
      str = str.replace(/,/g, "");
    }
  } else if (str.includes(",")) {
    // 2585,74
    str = str.replace(",", ".");
  }

  const number = Number(str);

  return Number.isFinite(number) ? number : 0;
}

export const toE164 = async (phone, countryIso) => {
  if (!phone || !countryIso) return undefined;

  let normalizedPhone = String(phone).trim();

  if (normalizedPhone.startsWith("00")) {
    normalizedPhone = `+${normalizedPhone.slice(2)}`;
  }

  try {
    const { parsePhoneNumberFromString } = await import("libphonenumber-js/min");
    const parsed = parsePhoneNumberFromString(
      normalizedPhone,
      countryIso.toUpperCase()
    );

    if (!parsed || !parsed.isValid()) {
      console.warn("Invalid phone number; omitted from tracking");
      return phone;
    }

    return parsed.number;
  } catch {
    console.warn("Phone number parsing failed; omitted from tracking");
    return phone;
  }
};
export async function trackPurchase(order, customer = {}) {
  if (!order?.reference) {
    return;
  }

  const key = `sg_purchase_${order.reference}`;

  // Prevent duplicate purchase events
  try {
    if (localStorage.getItem(key)) {
      return;
    }
  } catch (error) {
    // Ignore localStorage errors
  }

  // --------------------------------------------------
  // PRODUCTS
  // --------------------------------------------------

  const products = Array.isArray(order.products)
    ? order.products
    : [];

  const items = products
    .map((product) => {
      const price = parsePrice(product.price_each);

      return {
        item_id: String(
            product.reference ||
            product.product_reference ||
            product.id_product ||
            product.id ||
            ""
          ),

        item_name:
          product.name
            ?.split(" (Frames:")[0]
            ?.trim() || "",
         ...(product.photographer
            ? { item_brand: product.photographer }
            : {}),

          ...(product.collection || product.category
            ? {
                item_category:
                  product.collection || product.category,
              }
            : {}),
        price,

        quantity: Number(product.quantity) || 1,

        item_variant:
          Array.isArray(product.attributes)
            ? product.attributes
                .map(
                  (attr) =>
                    `${attr.value}`
                )
                .join(" / ")
            : undefined,
      };
    })
    .filter((item) => item.item_id);


  // --------------------------------------------------
  // ORDER TOTALS
  // --------------------------------------------------

  const total = parsePrice(order.total);

  const shipping = parsePrice(order.shipping);

  const tax = parsePrice(order.vat);


  // --------------------------------------------------
  // GA4 PURCHASE EVENT
  // --------------------------------------------------
    const phoneNumber = await toE164(
    customer?.phone || order?.billing_address?.phone,
    customer?.country_iso || order?.billing_address?.country_iso
);
  push(
    "purchase",
    {
      transaction_id: String(order.reference),

      // Final order total including shipping/tax
      value: round(total),

      // VAT
      tax: round(tax),

      // Shipping
      shipping: round(shipping),

      // Currency from API
      currency: order.currency || CURRENCY,

      // Optional coupon/voucher
      coupon:
        order.voucherCode || undefined,

      // Products
      items,
    },
    {
      // Used by GTM for Enhanced Conversions /
      // Meta Advanced Matching.
      // Not sent directly to GA4.
      user_data: {
        email:
          customer?.email ||
          order?.billing_address?.email ||
          undefined,

        phone_number:
          phoneNumber ||
          undefined,
      },
    }
  );


  // --------------------------------------------------
  // MARK ORDER AS TRACKED
  // --------------------------------------------------

  try {
    localStorage.setItem(key, "1");
  } catch (error) {
    // Ignore localStorage errors
  }
}
export function trackShippingAddress(address) {
  if (!address) return;

  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: "shipping_address_selected",
    ecommerce: {
      shipping_address: {
        country: address.countryName || "",
        city: address.city || "",
        postcode: address.postcode || "",
      },
    },
  });
}

export function trackBillingAddress(address) {
  if (!address) return;

  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: "billing_address_selected",
    ecommerce: {
      billing_address: {
        country: address.countryName || "",
        city: address.city || "",
        postcode: address.postcode || "",
      },
    },
  });
}

/**
 * Optional exports.
 *
 * Useful if you need these utilities elsewhere.
 */
export {
  push,
  sum,
  round,
  cartItems,
  CURRENCY,
};
